import { Pencil, Trash } from "@tamagui/lucide-icons";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useHubs, useUserRole, Hub, HubStatus, UserRole } from "common";
import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  YStack,
  XStack,
  Text,
  Spinner,
  Card,
  Separator,
  ScrollView,
  Button,
  View,
  useMedia,
} from "tamagui";
import { Table } from "tamagui-config";

import StationFormModal, {
  FormValues as CreateHubFormValues,
} from "./StationFormModal";
import ConfirmDialog from "../../components/ConfirmDialog";
import { uploadToS3 } from "../../utils/s3Upload";
import { setDocumentTitle } from "../../utils/setDocumentTitle";

interface LocationData {
  street?: string;
  city?: string;
  zip?: string;
  state?: string;
}

const formatLocation = (
  location: LocationData | string | null | undefined,
): string => {
  if (!location) return "";

  if (typeof location === "string") return location;

  const parts: string[] = [];

  if (location.street) parts.push(location.street);
  if (location.city) parts.push(location.city);
  if (location.state) parts.push(location.state);
  if (location.zip) parts.push(location.zip);

  return parts.join(", ");
};

const StatusIndicator = ({ status }: { status: string }) => (
  <View
    flexDirection="row"
    alignItems="center"
    gap="$2"
    theme={status?.toLowerCase() === "online" ? "green" : "orange"}
    backgroundColor="$color6"
    borderRadius={1000_000_000}
    paddingHorizontal="$2"
    paddingVertical="$1"
  >
    <View
      width={10}
      height={10}
      backgroundColor="$color10"
      borderRadius={1000}
    />
    <Text color="$color10" fontWeight="$2">
      {status}
    </Text>
  </View>
);

const ActionView = ({
  hub,
  onHubDelete,
  onHubEdit,
}: {
  hub: Hub;
  onHubDelete: (hub: Hub) => void;
  onHubEdit: (hub: Hub) => void;
}) => {
  const { permissions } = useUserRole();

  return (
    <View flexDirection="row" gap="$2" onPress={(e) => e.stopPropagation()}>
      <Button
        disabled={!permissions.hub.update}
        icon={<Pencil size="$1" />}
        onPress={(e) => {
          e.stopPropagation();
          onHubEdit(hub);
        }}
        disabledStyle={{
          cursor: "default",
          opacity: 0.5,
        }}
      ></Button>
      <Button
        disabled={!permissions.hub.delete}
        icon={<Trash size="$1" />}
        onPress={(e) => {
          e.stopPropagation();
          onHubDelete(hub);
        }}
        disabledStyle={{
          cursor: "default",
          opacity: 0.5,
        }}
      ></Button>
    </View>
  );
};

const BaseStations: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sm, xs } = useMedia();
  const rowCounter = useRef(-1);
  const [isCreateDialogOpen, setCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedHub, setSelectedHub] = useState<Hub | null>(null);
  const { fetchHubs, addHub, modifyHub, removeHub, hubs, error, loading } =
    useHubs({ fetchAndSubscribeOnLoad: true });
  const { role, permissions } = useUserRole();
  const columnHelper = createColumnHelper<Hub>();

  const handleRowClick = (hubId: string) => {
    navigate(`/base-stations/${hubId}/sensors`);
  };

  const onHubDelete = (hub: Hub) => {
    setSelectedHub(hub);
    setDeleteDialogOpen(true);
  };

  const onHubEdit = (hub: Hub) => {
    setSelectedHub(hub);
    setCreateDialogOpen(true);
  };

  const onHubAdd = () => {
    setSelectedHub(null);
    setCreateDialogOpen(true);
  };

  const handleHubSubmit = async (values: CreateHubFormValues) => {
    let logoUrl = values.logoUrl;

    if (values.logoFile) {
      try {
        const hubId = selectedHub?.id || "new";
        logoUrl = await uploadToS3(values.logoFile, hubId);
      } catch (error) {
        console.error("Error uploading logo:", error);
      }
    }

    const payload = {
      name: values.name,
      serialNumber: values.serialNumber,
      macAddress: values.macAddress,
      status: values.status as HubStatus,
      logoUrl,
    };

    if (selectedHub) {
      await modifyHub({ id: selectedHub.id, ...payload });
    } else {
      await addHub(payload);
    }
    setSelectedHub(null);
  };

  const handleDeleteHubSubmit = async (hub: Hub) => {
    await removeHub(hub.id);
    setSelectedHub(null);
  };

  const transformStatusValue = (status: string): string => {
    switch (status.toUpperCase()) {
      case HubStatus.Active:
        return t("baseStations.table.statusValue.active");
      case HubStatus.Inactive:
        return t("baseStations.table.statusValue.inactive");
      case HubStatus.Maintenance:
        return t("baseStations.table.statusValue.maintenance");
      case HubStatus.Pending:
        return t("baseStations.table.statusValue.pending");
      default:
        return t("baseStations.table.statusValue.unknown");
    }
  };

  const columns = [
    columnHelper.accessor("name", {
      header: () => t("baseStations.table.name"),
      cell: (info) => <Text>{info.getValue()}</Text>,
    }),
    columnHelper.display({
      id: "location",
      header: () => t("baseStations.table.location"),
      cell: ({ row }) => (
        <Text>
          {formatLocation({
            street: row.original.street,
            city: row.original.city,
            state: row.original.state,
            zip: row.original.zip,
          }) || t("baseStations.noLocation")}
        </Text>
      ),
    }),
    columnHelper.accessor("serialNumber", {
      header: () => t("baseStations.table.serialNumber"),
      cell: (info) => <Text>{info.getValue()}</Text>,
    }),
    columnHelper.accessor("status", {
      header: () => t("baseStations.table.status"),
      cell: (info) => (
        <StatusIndicator status={transformStatusValue(info.getValue())} />
      ),
    }),
    columnHelper.display({
      id: "actions",
      header: () => t("baseStations.table.action"),
      cell: ({ row }) => (
        <ActionView
          hub={row.original}
          onHubDelete={onHubDelete}
          onHubEdit={onHubEdit}
        />
      ),
    }),
  ];

  const table = useReactTable({
    data: hubs,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    setDocumentTitle({ hubName: selectedHub?.name });
  }, [selectedHub]);

  const renderContent = () => {
    if (loading) {
      return (
        <YStack flex={1} justifyContent="center" alignItems="center" space="$4">
          <Spinner size="large" />
          <Text>{t("baseStations.loading")}</Text>
        </YStack>
      );
    }

    if (error) {
      return (
        <YStack flex={1} justifyContent="center" alignItems="center">
          <Text color="$red10">{t("common.error")}</Text>
          <Text>{error.message}</Text>
          <Button marginTop="$4" onPress={fetchHubs}>
            {t("common.retry")}
          </Button>
        </YStack>
      );
    }

    if (!hubs || hubs.length === 0) {
      return (
        <YStack flex={1} justifyContent="center" alignItems="center">
          <Text>{t("baseStations.noBaseStations")}</Text>
          <Button marginTop="$4" onPress={fetchHubs}>
            {t("common.refresh")}
          </Button>
        </YStack>
      );
    }

    const headerGroups = table.getHeaderGroups();
    const tableRows = table.getRowModel().rows;
    const footerGroups = table.getFooterGroups();

    const allRowsLength =
      tableRows.length + headerGroups.length + footerGroups.length;
    rowCounter.current = -1;

    if (sm || xs) {
      return (
        <View
          width="100%"
          flexDirection="column"
          justifyContent="center"
          gap="$5"
          paddingVertical="$6"
        >
          {hubs.map((station) => (
            <View
              key={station.id}
              borderRadius="$4"
              borderWidth="$1"
              borderColor="$borderColor"
              flex={1}
              alignSelf="stretch"
              width="100%"
              gap="$2"
              paddingTop="$2"
              pressStyle={{ opacity: 0.8, scale: 0.98 }}
              onPress={() => handleRowClick(station.id)}
            >
              <XStack
                alignItems="center"
                paddingVertical="$1"
                marginLeft="$3"
                gap="$2"
              >
                <View justifyContent="space-between">
                  <Text>{station.name || t("baseStations.unnamed")}</Text>
                  <Text fontSize="$3" color="$gray11">
                    {formatLocation({
                      street: station.street,
                      city: station.city,
                      state: station.state,
                      zip: station.zip,
                    }) || t("baseStations.noLocation")}
                  </Text>
                </View>
                <View marginLeft="auto" paddingRight="$3">
                  <StatusIndicator status={station.status} />
                </View>
              </XStack>

              <View height={2} backgroundColor="$borderColor" />

              <View gap="$2">
                {Object.entries(station)
                  .filter(
                    ([name]) =>
                      ![
                        "name",
                        "status",
                        "id",
                        "macAddress",
                        "street",
                        "city",
                        "state",
                        "zip",
                      ].includes(name),
                  )
                  .map(([name, value], i, arr) => (
                    <View key={name}>
                      <View
                        flexDirection="row"
                        justifyContent="space-between"
                        marginHorizontal="$3"
                        paddingBottom="$2"
                      >
                        <Text>
                          {name.charAt(0).toUpperCase() + name.slice(1)}
                        </Text>
                        <Text color="$gray11">{value as string}</Text>
                      </View>
                      {i !== arr.length - 1 && <Separator />}
                    </View>
                  ))}
              </View>
            </View>
          ))}
        </View>
      );
    }

    return (
      <YStack flex={1}>
        <Table borderWidth={0} maxWidth="100%" gap="$3">
          <Table.Head>
            {headerGroups.map((headerGroup) => {
              rowCounter.current++;
              return (
                <Table.Row
                  rowLocation={
                    rowCounter.current === 0
                      ? "first"
                      : rowCounter.current === allRowsLength - 1
                        ? "last"
                        : "middle"
                  }
                  key={headerGroup.id}
                >
                  {headerGroup.headers.map((header) => (
                    <Table.HeaderCell key={header.id} borderWidth={0}>
                      <Text fontWeight="bold">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </Text>
                    </Table.HeaderCell>
                  ))}
                </Table.Row>
              );
            })}
          </Table.Head>
        </Table>
        <ScrollView>
          <Table
            cellWidth="$20"
            cellHeight="$8"
            borderWidth={0}
            maxHeight={600}
            maxWidth="100%"
            gap="$3"
          >
            <Table.Body>
              {tableRows.map((row) => {
                rowCounter.current++;
                return (
                  <Table.Row
                    hoverStyle={{
                      backgroundColor: "$color2",
                      cursor: "pointer",
                    }}
                    pressStyle={{
                      backgroundColor: "$color3",
                    }}
                    onPress={() => handleRowClick(row.original.id)}
                    rowLocation={
                      rowCounter.current === 0
                        ? "first"
                        : rowCounter.current === allRowsLength - 1
                          ? "last"
                          : "middle"
                    }
                    key={row.id}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Table.Cell key={cell.id} borderWidth={0}>
                        {cell.column.id === "status" ? (
                          flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )
                        ) : (
                          <Text theme="alt1">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </Text>
                        )}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </ScrollView>
      </YStack>
    );
  };

  return (
    <YStack flex={1}>
      <XStack justifyContent="space-between">
        <YStack space="$4">
          <Text fontSize="$8" fontWeight="bold">
            {t("baseStations.title")}
          </Text>
          <Text fontSize="$3" color="$gray11">
            {t("baseStations.description")}
          </Text>
        </YStack>
        {(role === UserRole.ADMIN || role === UserRole.INSTALLER) && (
          <Button
            onPress={onHubAdd}
            backgroundColor="$hgPrimary"
            size={"$4"}
            disabled={!permissions.hub.create}
          >
            {t("baseStations.addHub")}
          </Button>
        )}
      </XStack>
      <Separator marginTop="$4" />
      <Card flex={1} marginTop="$4" overflow="hidden">
        {renderContent()}
      </Card>

      <StationFormModal
        open={isCreateDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSubmit={handleHubSubmit}
        station={selectedHub}
      ></StationFormModal>

      <ConfirmDialog
        open={isDeleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={() => handleDeleteHubSubmit(selectedHub as Hub)}
        title={t("baseStations.confirm.title")}
        description={t("baseStations.confirm.description")}
      ></ConfirmDialog>
    </YStack>
  );
};

export default BaseStations;
